import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../lib/firebase";
import { createUserWithEmailAndPassword, updateProfile, signInWithCustomToken, sendEmailVerification } from "firebase/auth";
import { FaFacebook, FaArrowLeft } from "react-icons/fa";
import "../styles/Auth.css";

const authErrorMessages = {
  "auth/email-already-in-use": "An account with this email already exists. Try signing in instead.",
  "auth/invalid-email": "That email address doesn't look right.",
  "auth/weak-password": "Password should be at least 6 characters.",
  "auth/network-request-failed": "Network error — check your connection and try again.",
};
const friendlyAuthError = (err) => authErrorMessages[err.code] || err.message;

const SignupPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleFacebookSignup = async () => {
    setLoading(true);
    setError("");
    try {
      const loginUrl = "https://adsoar.cc/api/fb/login";
      const popup = window.open(loginUrl, "fb_login", "width=600,height=700,scrollbars=yes");

      const handleMessage = async (event) => {
        if (event.origin !== "https://adsoar.cc") return;
        if (event.data?.type === "FB_LOGIN_SUCCESS" && event.data.token) {
          window.removeEventListener("message", handleMessage);
          try {
            const result = await signInWithCustomToken(auth, event.data.token);
            onLogin(result.user);
          } catch (tokenErr) {
            setError("Failed to complete sign-in. Please try again.");
            setLoading(false);
          }
        }
      };

      window.addEventListener("message", handleMessage);

      const checkClosed = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener("message", handleMessage);
          setLoading(false);
        }
      }, 1000);
    } catch (err) {
      setError(err.message || "Facebook signup failed. Please try again.");
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      if (name) await updateProfile(userCredential.user, { displayName: name });
      try { await sendEmailVerification(userCredential.user); } catch (verifyErr) { console.error("Failed to send verification email:", verifyErr); }
      onLogin(userCredential.user);
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-root">

      {/* Left branding */}
      <div className="auth-left">
        <div className="auth-left-logo" onClick={() => navigate("/")}>Ad<span>soar</span></div>
        <div className="auth-left-body">
          <h1 className="auth-left-title">
            Run smarter<br />
            <span className="auth-left-gradient">Facebook ads.</span>
          </h1>
          <p className="auth-left-sub">
            A guided workflow to create, manage, and track campaigns — without getting lost in Meta Ads Manager.
          </p>
          <div className="auth-left-features">
            {["No credit card required", "Connect Meta account in minutes", "Cancel anytime"].map((f, i) => (
              <div className="auth-left-feature" key={i}>
                <span className="auth-left-check">✓</span>
                {f}
              </div>
            ))}
          </div>
        </div>
        <div className="auth-left-footer">© {new Date().getFullYear()} Adsoar</div>
      </div>

      {/* Right form */}
      <div className="auth-right">
        <div className="auth-form-wrap">
          <button className="auth-back" onClick={() => navigate("/")}>
            <FaArrowLeft style={{ fontSize: "0.75rem" }} /> Back to home
          </button>

          <h2 className="auth-title">Create your account</h2>
          <p className="auth-subtitle">Already have one? <button className="auth-toggle-link" onClick={() => navigate("/login")}>Sign in</button></p>

          <form onSubmit={handleSubmit}>
            <div className="auth-field">
              <label className="auth-label">Full Name</label>
              <input className="auth-input" type="text" value={name}
                onChange={e => setName(e.target.value)} placeholder="John Doe" required />
            </div>
            <div className="auth-field">
              <label className="auth-label">Email</label>
              <input className="auth-input" type="email" value={email}
                onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required />
            </div>
            <div className="auth-field">
              <label className="auth-label">Password</label>
              <input className="auth-input" type="password" value={password}
                onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
            </div>
            {error && <div className="auth-error">{error}</div>}
            <button className="auth-submit" type="submit" disabled={loading}>
              {loading ? "Creating account..." : "Create Account"}
            </button>
          </form>

          <div className="auth-divider">
            <span className="auth-divider-line" />
            <span className="auth-divider-text">or</span>
            <span className="auth-divider-line" />
          </div>

          <button className="auth-fb-btn" onClick={handleFacebookSignup} disabled={loading}>
            <FaFacebook style={{ fontSize: "1.1rem" }} />
            {loading ? "Connecting..." : "Continue with Facebook"}
          </button>

          <p className="auth-toggle" style={{ marginTop: 20, fontSize: "0.8rem", color: "#94a3b8" }}>
            By creating an account you agree to our{" "}
            <a href="/terms" style={{ color: "#6366f1", textDecoration: "none" }}>Terms</a> and{" "}
            <a href="/privacy" style={{ color: "#6366f1", textDecoration: "none" }}>Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
